/* global helcimProcess */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../../UserContext';
import { CreditCard, Check, Lock, Phone, Building2, Info, Loader2 } from 'lucide-react';
import './SubscriptionSection.css';
import { initializeDebugProxies } from '../../utils/debug-proxy';
import isoCountries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { useMemo } from "react";

isoCountries.registerLocale(enLocale);

// --- Tooltip Component ---
const Tooltip = ({ content }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  return (
    <div 
      className="tooltip-wrapper"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <Info className="tooltip-icon" />
      {isVisible && (
        <div className="tooltip-popup">
          {content}
          <div className="tooltip-arrow"></div>
        </div>
      )}
    </div>
  );
};

const SubscriptionSection = ({ onBack }) => {
  // --- Context & State ---
  const userContextValue = useContext(UserContext);
  
  const [selectedPlan, setSelectedPlan] = useState('core'); // 'core' or 'enterprise'
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  // Signup ends signed out, so this flow clears the session rather than
  // establishing one.
  const logout = userContextValue.logout;
  const totalSteps = 4;
    const step = 4;
    const progressPct = Math.round((step / totalSteps) * 100);

  const [billing, setBilling] = useState({
  name: "",
  street: "",
  city: "",
  province: "",
  postal: "",
  country:"CAN"
});
  // Debug refs
  const debugHistoryRef = useRef([]);
  const MAX_DEBUG_ENTRIES = 50;

  // --- Configuration ---
  const CORE_PRICE = process.env.REACT_APP_BUSINESS_PRICE || '347';
  const CORE_PLAN_ID = process.env.REACT_APP_BUSINESS_ID;
  // --- Configuration ---
const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }
];

const CA_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" }
];
const isUSA = billing.country === "USA";
const isCAN = billing.country === "CAN";

const regionLabel = isUSA ? "State" : isCAN ? "Province/Territory" : "State/Province/Region";
const postalLabel = isUSA ? "ZIP Code" : isCAN ? "Postal Code" : "Postal / ZIP";

const postalPlaceholder = isUSA ? "12345" : isCAN ? "A1A 1A1" : "Postal / ZIP";

const postalPattern = isUSA
  ? "^\\d{5}(-\\d{4})?$"
  : isCAN
  ? "^[A-Za-z]\\d[A-Za-z][- ]?\\d[A-Za-z]\\d$"
  : undefined;

// Reset region when country changes (prevents ON staying when user switches to USA)
useEffect(() => {
  setBilling(b => ({ ...b, province: "" }));
}, [billing.country]);

  // --- Debug Logic ---
  const updateDebugInfo = (info) => {
    try {
        debugHistoryRef.current.push({ timestamp: new Date().toISOString(), ...info });
        if (debugHistoryRef.current.length > MAX_DEBUG_ENTRIES) {
            debugHistoryRef.current = debugHistoryRef.current.slice(-MAX_DEBUG_ENTRIES);
        }
        localStorage.setItem('helcimDebugInfo', JSON.stringify({ 
            lastUpdate: new Date().toISOString(), 
            lastType: info.type 
        }));
    } catch (e) { console.warn('Debug storage failed:', e); }
  };

  // --- Helcim Initialization ---
    useEffect(() => {
        initializeDebugProxies();
        const errorObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1 || node.nodeType === 3) {
                        const text = node.textContent || node.innerText || '';
                        if (text.includes('ERROR:') && text.includes('(')) {
                            console.log('Helcim error detected:', text);
                            if (node.nodeType === 1) { node.style.display = 'none'; } else if (node.parentElement) { node.parentElement.style.display = 'none'; }
                            const errorMatch = text.match(/ERROR:\s*\((.*?)\)/);
                            if (errorMatch) {
                                const errorMsg = errorMatch[1];
                                let userMessage = errorMsg;
                                if (errorMsg.toLowerCase().includes('invalid credit card number')) { userMessage = 'Invalid card number. Please check your card details.'; }
                                else if (errorMsg.toLowerCase().includes('invalid')) { userMessage = 'Invalid card information. Please check your details.'; }
                                setErrorMessage(userMessage);
                                setIsProcessing(false);
                                setLoadingMessage('');
                            }
                        }
                    }
                });
            });
        });
    errorObserver.observe(document.body, { childList: true, subtree: true, characterData: true });

    // 2. Load Helcim Script
        const initializeHelcim = () => {
            try {
                updateDebugInfo({ type: 'initialization', helcimJsUrl: process.env.REACT_APP_HELCIM_JS_URL, helcimJsToken: process.env.REACT_APP_HELCIM_JS_TOKEN });
                console.error('=== Helcim.js Initialization Start ===');
                const existingScript = document.querySelector('script[src*="helcim"]');
                if (existingScript) { console.error('Removing existing Helcim script'); existingScript.remove(); }
                const script = document.createElement('script');
                script.src = process.env.REACT_APP_HELCIM_JS_URL;
                script.type = 'text/javascript';
                script.async = false;
                script.addEventListener('error', (error) => { console.error('=== Helcim.js Script Load Error ==='); console.error('Error details:', error); updateDebugInfo({ type: 'script_load_error', error: { message: error.message, stack: error.stack } }); });
                script.onload = () => {
                    console.error('=== Helcim.js Script Loaded ===');
                    const configToken = document.getElementById('token')?.value;
                    const helcimResults = document.getElementById('helcimResults');
                    const formConfig = { token: configToken || 'MISSING', helcimResults: helcimResults ? 'FOUND' : 'MISSING', timestamp: new Date().toISOString() };
                    console.error('Form configuration:', formConfig);
                    updateDebugInfo({ type: 'script_loaded', formConfig });
                    if (typeof window.helcimProcess !== 'function') { const error = 'helcimProcess function not found after script load'; console.error('ERROR:', error); setErrorMessage('Payment system not properly initialized. Please refresh the page.'); }
                    else { console.error('helcimProcess function available'); updateDebugInfo({ type: 'helcim_process_available' }); }
                    if (!configToken || !helcimResults) { const error = 'Required Helcim.js elements missing'; console.error('ERROR:', error); setErrorMessage('Payment form configuration error. Please refresh the page.'); }
                };
                script.onerror = () => { const error = 'Failed to load Helcim.js'; console.error('=== Helcim.js Load Error ==='); console.error(error); setErrorMessage('Failed to load payment system. Please refresh the page.'); };
                console.error('Appending Helcim script to body');
                document.body.appendChild(script);
            } catch (error) { console.error('=== Helcim.js Initialization Error ==='); console.error('Error:', error); setErrorMessage('Error initializing payment system. Please refresh the page.'); }
        };


    initializeHelcim();

        return () => {
            console.error('=== Cleaning up Helcim.js ===');
            const script = document.querySelector('script[src*="helcim"]');
            if (script) { script.remove(); }
            errorObserver.disconnect();
        };
    }, []);
    window.helcimCallback = async (response) => {
        console.log('helcimCallback triggered with response:', response);
        try {
            const helcimErrors = document.querySelectorAll('.helcim-error, [class*="error"]:not(.status-message)');
            helcimErrors.forEach(el => { if (el.textContent && el.textContent.includes('ERROR:')) { el.style.display = 'none'; } });
            if (response.response !== "1" && response.response !== 1) {
                const error = response.responseMessage || 'Card verification failed';
                updateDebugInfo?.({ type: 'helcim_error', error: { message: error, response } });
                let userMessage = error;
                if (error.toLowerCase().includes('declined')) { userMessage = 'Your card was declined. Please try a different card.'; }
                else if (error.toLowerCase().includes('invalid credit card number')) { userMessage = 'Invalid card number. Please check your card details and try again.'; }
                else if (error.toLowerCase().includes('invalid')) { userMessage = 'Invalid card information. Please check your details and try again.'; }
                else if (error.toLowerCase().includes('expired')) { userMessage = 'Your card appears to be expired. Please check the expiration date.'; }
                else if (error.toLowerCase().includes('cvv')) { userMessage = 'Invalid CVV. Please check the 3 or 4 digit code on your card.'; }
                setIsProcessing?.(false); setLoadingMessage?.(''); setErrorMessage?.(userMessage); throw new Error(userMessage);
            }
            const helcimResults = document.getElementById('helcimResults');
            if (!helcimResults) { throw new Error('Helcim results container not found'); }
            const hiddenInputs = helcimResults.querySelectorAll('input[type="hidden"]');
            const helcimData = {};
            hiddenInputs.forEach(input => { helcimData[input.name] = input.value; });
            updateDebugInfo?.({ type: 'helcim_results', helcimData });
            if (helcimData.response !== "1" && helcimData.response !== 1) { throw new Error(helcimData.responseMessage || 'Card verification failed'); }
            const cardToken = helcimResults.querySelector('input[name="cardToken"]')?.value;
            const customerCode = helcimResults.querySelector('input[name="customerCode"]')?.value;
            if (!cardToken) { throw new Error('Card verification failed: No token received'); }
            if (!customerCode) { throw new Error('Card verification failed: No customer code received'); }
            if (window.helcimCallbackResolve) { window.helcimCallbackResolve({ cardToken, customerCode }); window.helcimCallbackResolve = null; }
        } catch (error) {
            updateDebugInfo?.({ type: 'callback_error', error: { message: error.message, stack: error.stack } });
            setErrorMessage?.(error.message || 'An error occurred during card verification');
            setIsProcessing?.(false); setLoadingMessage?.('');
            if (window.helcimCallbackReject) { window.helcimCallbackReject(error); window.helcimCallbackReject = null; }
        }
    };

  // --- Process Payment ---
  const processPayment = async () => {
    // Wrap Helcim Process in a Promise
    const helcimResponse = await new Promise((resolve, reject) => {
        window.helcimCallbackResolve = resolve; window.helcimCallbackReject = reject; helcimProcess(); });

    // If we get here, the Form Jack worked and resolved the promise
    const signupToken = document.querySelector('input[name="signup_token"]')?.value;

    const billingDetails = {
    name: billing.name,
    street_1: billing.street,
    city: billing.city,       
    province: billing.province,
    postal_code: billing.postal,
    country: billing.country
    };

    const eventData = {
        event_type: "PURCHASE_SUBSCRIPTION",
        user_id: "0",
        product: {
            price: parseFloat(CORE_PRICE),
            interval: 'monthly',
            trial: true,
            plan_id: parseInt(CORE_PLAN_ID)
        },
        chargeable: {
            customer_code: helcimResponse.customerCode,
            card_token: helcimResponse.cardToken,
            billing_details: billingDetails
        },
        customer_tools: { selected_tools: [] } 
    };

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/signup/finalize`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, 
        body: JSON.stringify({ 
            event_data: eventData, 
            signup_token: signupToken, 
            plan_type: 'business' 
        }) 
    });

    const responseText = await response.text();
    if (!response.ok) throw new Error('Subscription setup failed');
    
    const data = JSON.parse(responseText);
    if (data.status === 'success') {
        // Signup deliberately does NOT log the user in. The backend issues no
        // token, and the temporary password is emailed instead, the user has
        // to come back through /login with it, which proves they can read that
        // mailbox. Clear any stale session so the confirmation screen is
        // genuinely signed out.
        logout();

        setLoadingMessage('Account created! Redirecting...');
        setTimeout(() => {
            window.location.href = data.redirect || '/signup/check-email';
        }, 1200);
    }
  };
  const countryOptions = useMemo(() => {
  const names = isoCountries.getNames("en", { select: "official" }); // alpha-2 -> name
  return Object.entries(names)
    .map(([alpha2, name]) => ({
      name,
      alpha3: isoCountries.alpha2ToAlpha3(alpha2)
    }))
    .filter(x => x.alpha3)
    .sort((a, b) => a.name.localeCompare(b.name));
}, []);

  const handleFormSubmit = async (e) => {
      e.preventDefault(); // Prevent standard React submit
      setErrorMessage('');
      setIsProcessing(true);
      setLoadingMessage('Verifying Card...');

      // 1. Manual Validation
      const mm = document.getElementById('cardExpiryMonth').value;
      const yy = document.getElementById('cardExpiryYear').value;
      
      if(!mm || !yy) {
          setErrorMessage("Please enter expiry date.");
          setIsProcessing(false);
          return;
      }

      // 2. THE FORM JACK: Intercept Helcim's submit attempt
      const form = document.getElementById('helcimForm');
      if (form) {
          form.submit = function() {
              console.log("NexOptA Debug: Form submit intercepted!");
                setTimeout(() => {
                const pageErrors = document.body.textContent;
                if (pageErrors.includes('ERROR:') && pageErrors.includes('(')) {
                    // Extract error message
                    const errorMatch = pageErrors.match(/ERROR:\s*\((.*?)\)/);
                    const errorMsg = errorMatch ? errorMatch[1] : 'Card verification failed';
                    
                    // Clean up Helcim's error display
                    const errorElements = Array.from(document.querySelectorAll('*')).filter(el => 
                        el.textContent.includes('ERROR:') && el.children.length === 0
                    );
                    errorElements.forEach(el => el.style.display = 'none');
                    
                    // Call the callback with error response
                    if (window.helcimCallback) {
                        window.helcimCallback({
                            response: "0",
                            responseMessage: errorMsg
                        });
                    }
                    return false;
                }
              // Scrape the tokens Helcim just injected
                const cardToken = document.querySelector('[name="cardToken"]')?.value;
                const customerCode = document.querySelector('[name="customerCode"]')?.value;

              // Resolve the promise if we found tokens

                if (cardToken && customerCode) {
                    window.helcimCallbackResolve({ cardToken, customerCode });
                } else {
                    // No token means error occurred
                    if (window.helcimCallback) {
                        window.helcimCallback({
                            response: "0",
                            responseMessage: "Card verification failed"
                        });
                    }
                }
            }, 100); // Small delay to let Helcim update the DOM
            
              
              return false; // STOP the page from reloading
          };
      }

      try {
          await processPayment();
      } catch (err) {
          console.error(err);
          setErrorMessage(err.message || "Payment failed");
          setIsProcessing(false);
      }
  };

  return (
    <div className="subscription-section">
      <div className="nx-topbar">
        <div className="nx-brand">
          {/* Replace this with <img src="/your-logo.svg" .../> if you want */}
          <div className="nx-brand-mark">
          <img
            className="nx-brand-logo"
            src="/images/NexOpta_Icon.png"
            alt="Nexopta"
          /></div>
          <div className="nx-brand-word">NEXOPTA</div>
        </div>

        <div className="nx-step">
          Step {step} of {totalSteps}
        </div>
      </div>

      {/* PROGRESS BAR */}
      <div className="nx-progress-wrap">
        <div className="nx-progress-track">
          <div className="nx-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <div className="sub-container">

        <div className="sub-grid">
          {/* --- LEFT COLUMN: Plans & Form --- */}
          <div className="left-col">
    
            <div className="header-area">
                <h2 className="main-title">Choose your plan</h2>
                <p className="sub-title">Simple, transparent pricing for your business.</p>
            </div>
    
            {/* Plan Cards Container */}
            <div className="cards-container">
              
              {/* Core Plan Card */}
              <div
                onClick={() => setSelectedPlan('core')}
                className={`plan-card ${selectedPlan === 'core' ? 'core-selected' : ''}`}
              >
                <div className="badge-popular">Most Popular</div>
                <div className="card-header-flex">
                    <div>
                        <div className="title-row">
                            <h2 className="plan-name">Foundation Plan</h2>
                        </div>
                    </div>
                    <div className="pricing-block">
                      <div className="plan-price">
                        <span className="price-val">${CORE_PRICE}</span>
                        <span className="price-unit">/month</span>
                        </div>
                        <div className="fees-note">
                            + usage fees
                            <Tooltip content="Agentic workflows incur variable costs. Your plan includes a generous allowance to get you started." />
                        </div>
                    </div>
                  </div>
                <div className="fp-pricing-header">
                  <p className="fp-trial-tag">14-day free trial</p>
                  <p className="fp-tagline">For leaders ready to put AI to work across daily operations. Less than a part-time admin. Available 24/7.</p>
                  <p className="fp-risk">Risk-free. Cancel anytime.</p>
                </div>
                <div className="fp-included-box">
                  <h3 className="fp-section-heading">What's included</h3>
                  <ul className="fp-included-list">
                    {[
                      'Nexopta Platform',
                      '1 Standard Package',
                      '$25/month Usage Credits',
                      '1GB Core Knowledge Base',
                      'AgentOps Studio + 1 Agent Slot',
                      'Done-for-you Setup'
                    ].map((item, i) => (
                      <li key={i} className="fp-included-item">
                        <Check className="fp-included-check" size={15} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>



                <hr className="divider" style={{ marginTop: '1.5rem' }} />

                {/* Key Features */}
                <div className="fp-features-box">
                  <h3 className="fp-section-heading">Key Features</h3>
                  <div className="fp-features-list">
                    {[
                      { title: 'Built for Real Business Operations', desc: "Nexopta's Business Assistant connects to your systems, understands your business context, and takes action, not just answers questions." },
                      { title: 'Connected to Your Existing Systems', desc: 'Works through your included toolkits to connect with the tools you already run your business on (CRM, email, calendars, docs, and more).' },
                      { title: 'Context-Aware Intelligence', desc: 'Uses your Core Knowledge Base and connected toolkits to deliver accurate, consistent responses grounded in your real business information.' },
                      { title: 'Intelligent Knowledge Base', desc: 'Centralizes your SOPs, docs, and internal knowledge so your assistant stays aligned and reliable as your business evolves.' },
                      { title: 'Voice & Chat Enabled', desc: 'Ask questions, request changes, and trigger actions using natural conversation.' },
                      { title: 'Automation That Adapts', desc: 'Choose how automation runs. The Autonomous Agent Package gives you three background agents to monitor, trigger, and complete separate jobs. The Business Assistant Starter Package adds a conversational Business Assistant plus two agents, so you can request actions in chat while agents run ongoing work behind the scenes.' },
                      { title: 'Enterprise Security', desc: 'Your data stays protected with encryption and account-level controls, with activity visibility to support accountability.' },
                      { title: 'Scales With Your Business', desc: 'Start with the Foundation Plan to build your company knowledge base, then expand over time with more agents, tools, and workflows as you grow.' },
                    ].map((feat, i) => (
                      <div key={i} className="fp-feat-item">
                        <p className="fp-feat-title">{feat.title}</p>
                        <p className="fp-feat-desc">{feat.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                                <hr className="divider" style={{ marginTop: '1.5rem' }} />

                                <button className={`btn-select ${selectedPlan === 'core' ? 'active' : 'inactive'}`}>
                  {selectedPlan === 'core' ? 'Selected' : 'Get started for free'}
                  {selectedPlan === 'core' && <Check size={16} />}
                </button>

              </div>
    
              {/* Enterprise Plan Card */}
              <div 
                onClick={() => setSelectedPlan('enterprise')}
                className={`plan-card ${selectedPlan === 'enterprise' ? 'ent-selected' : ''}`}
              >
                <div className="card-header-flex">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div className="ent-icon-box">
                            <Building2 className="text-gray-600" size={24} />
                        </div>
                        <div>
                            <h2 className="plan-name" style={{ fontSize: '1.25rem' }}>Enterprise Plan</h2>
                            <p className="plan-desc">Custom solutions for organizations</p>
                        </div>
                    </div>
                    <div className="ent-pricing">
                        <span className="ent-pricing-title">Custom Pricing</span>
                        <p className="ent-pricing-sub">Tailored to your needs</p>
                    </div>
                </div>
    
                <div className="feat-grid">
                    {[
                    'Unlimited AI Agents',
                    'Enterprise Knowledge Base',
                    'Dedicated Account Manager',
                    'Custom Integrations',
                    'Advanced Security',
                    'Custom SLA'
                    ].map((feat, i) => (
                        <div key={i} className="ent-feat-row">
                            <Check className="ent-check" />
                            {feat}
                        </div>
                    ))}
                </div>
              </div>
            </div>
    
            {/* Conditional: Payment Form or Contact */}
            {selectedPlan === 'core' ? (
              <div style={{ marginTop: '2rem' }}>
                  <h3 className="secure-heading">
                      <Lock size={16} /> Secure Payment
                  </h3>

                  {/* Status Messages */}
                  {(errorMessage || isProcessing) && (
                      <div className={`status-msg ${errorMessage ? 'error' : 'loading'}`}>
                          {isProcessing ? <Loader2 size={20} className="animate-spin"/> : <Info size={20}/>}
                          <span>{errorMessage || loadingMessage}</span>
                      </div>
                  )}

                  <form id="helcimForm" name="helcimForm"  onSubmit={handleFormSubmit} className="payment-form-container">
                      {/* Helcim Results Container - Required for Token Injection */}
                      <div id="helcimResults"></div>
                      
                    <input type="hidden" id="token" value={process.env.REACT_APP_HELCIM_JS_TOKEN} />
                    <input type="hidden" name="plan_type" value={selectedPlan} />
                    <input type="hidden" name="signup_token" value={new URLSearchParams(window.location.search).get('token')} />
                    <input type="hidden" id="setAsDefaultPaymentMethod" name="setAsDefaultPaymentMethod" value="1" />
                    {/* REQUIRED by Helcim.js for AVS */}
                    <input type="hidden" id="cardHolderAddress" value={billing.street} />
                    <input type="hidden" id="cardHolderPostalCode" value={billing.postal} />

                    {/* OPTIONAL billing fields (ok to keep) */}
                    <input type="hidden" id="billing_street1" value={billing.street} />
                    <input type="hidden" id="billing_city" value={billing.city} />
                    <input type="hidden" id="billing_province" value={billing.province} />
                    <input type="hidden" id="billing_country" value={billing.country} />
                    <input type="hidden" id="billing_postalCode" value={billing.postal} />

                      {/* Billing Address */}
                      <div className="billing-group">
                          <h4 className="billing-title">Billing Information</h4>
                          
                        <div className="form-group">
                            <label className="form-label">Full Name</label>
                            <input type="text" id="billingName" onChange={(e) => setBilling(b => ({ ...b, name: e.target.value }))} required placeholder="Full Name" className="form-input" />
                        </div>

                          <div className="form-group">
                              <label className="form-label">Street Address</label>
                              <input type="text" id="billingStreetAddress" onChange={(e) => setBilling(b => ({ ...b, street: e.target.value }))} required placeholder="123 Main St" className="form-input" />
                          </div>
                    <div className="form-row-3">
                    <div>
                        <label className="form-label">City</label>
                        <input
                        type="text"
                        id="billingCity"
                        className="form-input"
                        required
                        placeholder="Toronto"
                        onChange={(e) => setBilling(b => ({ ...b, city: e.target.value }))}
                        />
                    </div>

                    <div>
                        <label className="form-label">{regionLabel}</label>

                        {isUSA ? (
                        <select
                            id="billingProvince"
                            className="form-input"
                            required
                            value={billing.province}
                            onChange={(e) => setBilling(b => ({ ...b, province: e.target.value }))}
                        >
                            <option value="">Select a state</option>
                            {US_STATES.map(s => (
                            <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                            ))}
                        </select>
                        ) : isCAN ? (
                        <select
                            id="billingProvince"
                            className="form-input"
                            required
                            value={billing.province}
                            onChange={(e) => setBilling(b => ({ ...b, province: e.target.value }))}
                        >
                            <option value="">Select a province/territory</option>
                            {CA_PROVINCES.map(p => (
                            <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
                            ))}
                        </select>
                        ) : (
                        <input
                            type="text"
                            id="billingProvince"
                            className="form-input"
                            required
                            placeholder="Region"
                            value={billing.province}
                            onChange={(e) => setBilling(b => ({ ...b, province: e.target.value }))}
                        />
                        )}
                    </div>

                    <div>
                        <label className="form-label">{postalLabel}</label>
                        <input
                        type="text"
                        id="billingPostalCode"
                        className="form-input"
                        required
                        placeholder={postalPlaceholder}
                        onChange={(e) => setBilling(b => ({ ...b, postal: e.target.value }))}
                        pattern={postalPattern}
                        title={isUSA ? "ZIP: 12345 or 12345-6789" : isCAN ? "Postal: A1A 1A1" : ""}
                        inputMode={isUSA ? "numeric" : "text"}
                        />
                    </div>
                    </div>

                    <div className="form-group">
                    <label className="form-label">Country</label>
                    <select
                        id="billingCountry"
                        className="form-input"
                        value={billing.country}
                        onChange={(e) => setBilling(b => ({ ...b, country: e.target.value }))}
                        required
                    >
                        {countryOptions.map(c => (
                        <option key={c.alpha3} value={c.alpha3}>
                            {c.name} ({c.alpha3})
                        </option>
                        ))}
                    </select>
                    </div>
                      </div>

                      {/* Card Details */}
                      <div className={`card-section ${isProcessing ? 'is-processing' : ''}`}>
                      <div className="form-group">
                          <label className="form-label">Cardholder Name (On Card)</label>
                          <input type="text" id="cardHolderName" required placeholder="John Doe" className="form-input" />
                      </div>
                      
                      <div className="form-group">
                          <label className="form-label">Card Number</label>
                          <div className="input-wrapper">
                              <input type="text" id="cardNumber" required placeholder="0000 0000 0000 0000" className="form-input has-icon" />
                              <CreditCard className="input-icon" />
                          </div>
                      </div>
    
                      <div className="form-row-2">
                          <div>
                              <label className="form-label">Expiry Date</label>
                              <div className="date-inputs">
                                <input type="text" id="cardExpiryMonth" required placeholder="MM" maxLength="2" className="form-input text-center" />
                                <span className="divider-slash">/</span>
                                <input type="text" id="cardExpiryYear" required placeholder="YY" maxLength="2" className="form-input text-center" />
                              </div>
                          </div>
                          <div>
                              <label className="form-label">CVC</label>
                              <input type="text" id="cardCVV" required placeholder="123" maxLength="4" className="form-input text-center" />
                          </div>
                      </div>
                        </div>
                      <button type="submit" disabled={isProcessing} className="btn-primary">
                          {isProcessing ? 'Processing Payment...' : 'Start Your 14-Day Free Trial'}
                      </button>
                      <p className="trial-note">You won't be charged until your trial ends. Cancel anytime.</p>
                  </form>
              </div>
            ) : (
              /* Enterprise View */
              <div className="enterprise-contact-box" style={{ marginTop: '2rem' }}>
                  <Phone className="ent-big-icon" />
                  <h3 className="ent-heading">Let's Talk About Your Needs</h3>
                  <p className="ent-text">
                    Our enterprise team will work with you to create a custom solution that fits your organization's specific requirements.
                  </p>
                  <a href="tel:+1-888-555-0123" className="btn-contact">
                    <Phone size={20} /> Call for Enterprise Pricing
                  </a>
                  <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    Or email <a href="mailto:enterprise@nexopta.com" style={{ color: '#179da6' }}>enterprise@nexopta.com</a>
                  </p>
              </div>
            )}
          </div>
    
          {/* --- RIGHT COLUMN: Info Side Panel --- */}
          <div className="right-col">
          <div className="nx-info-card">
            <div className="nx-info-header">
              <div className="nx-info-badge">
                <Info size={16} />
              </div>
              <h3>Why we need this</h3>
            </div>

                <p className="info-text">
                    {selectedPlan === 'core' 
                      ? <span>We collect payment information to activate your subscription. Your <strong>14-day free trial</strong> starts immediately.</span>
                      : "Enterprise solutions require a personalized approach."
                    }
                </p>
          </div>
          
            <div className="security-box">
                <p className="sec-text">
                    By proceeding, you agree to Nexopta's Terms of Service and Privacy Policy. All transactions are secure and encrypted.
                </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSection;
